import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from 'common/google/util';
import { fetchWithFailover } from 'default-client/DefaultClient';
import { getErrorMessage } from 'common/errors/errorUtils';

type WebAccountError = {
  title: string;
  status: number;
  type: string;
};

// eslint-disable-next-line no-shadow
export enum ChangePasswordState {
  REQUESTING_TOKEN,
  TOKEN_REQUESTED,
  TOKEN_VALIDATED,
  PASSWORD_UPDATED,
  INVALID_SECRET,
  INVALID_TOKEN,
}

export const useChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState(ChangePasswordState.REQUESTING_TOKEN);
  const [phone, setPhone] = useState('');
  const [validToken, setValidToken] = useState('');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const secret = queryParams.get('secret') || '';
  const email = queryParams.get('email') || '';

  useEffect(() => {
    const requestToken = async () => {
      await requestSmsToken();
    };
    if (!secret || !email) {
      changeState(ChangePasswordState.INVALID_SECRET);
      return;
    }
    requestToken();
  }, [secret, email]);

  const track = (eventAction: string, eventLabel: string) => {
    trackEvent({
      eventCategory: 'change password',
      eventAction,
      eventLabel,
    });
  };

  const isResponseOk = (response: Response | undefined) => response && response.ok;

  const handleException = () => {
    // Something went wrong, try again later
    setError(getErrorMessage());
  };

  const handleError = async (response: Response) => {
    const errorData: WebAccountError = await response.json();
    const { type } = errorData;
    if (type === 'urn:verisure:webaccount/invalid-password-reset-secret') {
      changeState(ChangePasswordState.INVALID_SECRET);
      return;
    }
    if (type === 'urn:verisure:webaccount/invalid-token') {
      changeState(ChangePasswordState.INVALID_TOKEN);
      return;
    }
    const errorMsg = getErrorMessage(type);
    setError(errorMsg);
  };

  const changeState = (newState: ChangePasswordState) => {
    setState(newState);
    setError('');
  };

  const startRequest = () => {
    setIsLoading(true);
    setError('');
  };

  const requestSmsToken = async () => {
    try {
      startRequest();
      changeState(ChangePasswordState.REQUESTING_TOKEN);
      const response = await fetchWithFailover(`/webaccount/public/${email}/password/forgot/smstoken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          APPLICATION_ID: process.env.REACT_APP_APPLICATION_ID,
        },
        body: JSON.stringify({ secret }),
      });
      if (!isResponseOk(response)) {
        await handleError(response);
        track('requestSmsToken', 'invalid secret');
        changeState(ChangePasswordState.INVALID_SECRET);
      } else {
        const { maskedPhoneNumber } = await response.json();
        track('requestSmsToken', 'success');
        changeState(ChangePasswordState.TOKEN_REQUESTED);
        setPhone(maskedPhoneNumber);
      }
    } catch {
      handleException();
      track('requestSmsToken', 'request failed');
      changeState(ChangePasswordState.INVALID_SECRET);
    } finally {
      setIsLoading(false);
    }
  };

  const validateSmsToken = async (smsToken: string) => {
    try {
      startRequest();
      const response = await fetchWithFailover(`/webaccount/public/${email}/password/forgot/smstoken/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          APPLICATION_ID: process.env.REACT_APP_APPLICATION_ID,
        },
        body: JSON.stringify({ secret, smsToken }),
      });
      if (!isResponseOk(response)) {
        await handleError(response);
        track('validateSmsToken', 'invalid');
      } else {
        track('validateSmsToken', 'success');
        setValidToken(smsToken);
        changeState(ChangePasswordState.TOKEN_VALIDATED);
      }
    } catch {
      handleException();
      track('validateSmsToken', 'request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (password: string) => {
    try {
      startRequest();
      const response = await fetchWithFailover(`/webaccount/public/${email}/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          APPLICATION_ID: process.env.REACT_APP_APPLICATION_ID,
        },
        body: JSON.stringify({ secret, smsToken: validToken, password }),
      });
      if (!isResponseOk(response)) {
        await handleError(response);
        track('changePassword', 'invalid');
      } else {
        track('changePassword', 'success');
        changeState(ChangePasswordState.PASSWORD_UPDATED);
      }
    } catch {
      handleException();
      track('changePassword', 'request failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetError = () => {
    setError('');
  };

  return {
    requestSmsToken,
    validateSmsToken,
    changePassword,
    isLoading,
    state,
    error,
    resetError,
    phone,
  };
};
